/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AnyValidator, IntegerValidator, NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  ActionMenu,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  Button,
  ButtonProps,
  DROPDOWN_POSITION_LEFT,
  DropdownItemData,
  ICON_ADD,
  ICON_DELETE,
  TOOLTIP_POSITION_TOP,
  Tooltip,
} from '@squiz/sds';
import { EntitySegment } from 'apps/customer-data-platform/src/Services/segmentation';
import { RegExpValidator } from 'apps/customer-data-platform/src/Utilities/RegExpValidator/RegExpValidator';
import { isEqual } from 'lodash';
import isFunction from 'lodash/isFunction';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import set from 'lodash/set';
import uniq from 'lodash/uniq';
import React, { ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import {
  FormFieldList,
  FormFieldValidationResponse,
  useFormValidation,
} from '../../../../Hooks/UseFormValidation/UseFormValidation.hook';
import { useSegmentBuilderForm } from '../../../../Hooks/UseSegmentBuilderForm/UseSegmentBuilderForm.hook';
import { SEGMENT_BUILDER_DATA_IDS } from '../../SegmentBuilder.constants';
import { UISegmentRuleProperties } from '../../defs/UISegmentRuleProperties.defs';
import { SelectRuleOperator } from '../../elements/SelectRuleOperator/SelectRuleOperator';
import { SelectRulesetOperator } from '../../elements/SelectRulesetOperator/SelectRulesetOperator';
import { createRulesetsForAPI } from '../../utils/CreateRulesetsForAPI/CreateRulesetsForAPI';
import { SegmentDetails } from '../SegmentDetails/SegmentDetails';
import { SegmentRule } from '../SegmentRule/SegmentRule';
import { SegmentBuilderFormProps } from './SegmentBuilderForm.props';

const ICON_ADD_ROUNDED = (
  <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path>
  </svg>
);

interface ChangeSegmentState {
  segment: EntitySegment;
  response: FormFieldValidationResponse;
}

/**
 * The form for creating or updating a segment.
 */
export const SegmentBuilderForm = (props: SegmentBuilderFormProps): ReactElement => {
  const { t } = useTranslation();

  // eslint-disable-next-line react/prop-types
  const { data, events, errors: initialErrors, groupCount, internalId, onChange, onDelete, schema } = props;
  const {
    conditions,
    currentDescription,
    currentId,
    currentName,
    handleAddRule,
    handleAddRuleset,
    handleDeleteRule,
    handleDeleteRuleset,
    handleRuleInputsChange,
    handleRuleOperatorFirst,
    handleRuleOperatorLast,
    handleRuleSetOperator,
    handleRuleSelectsChange,
    rulesetOperator,
  } = useSegmentBuilderForm(data);

  // Set the state for initial props to update
  const [name, setName] = useState(currentName ? currentName : '');
  const [id, setId] = useState(currentId ? currentId : '');
  const [internalErrors, setInternalErrors] = useState<Record<string, string | null>>({
    [`id-${internalId}`]: '',
    [`name-${internalId}`]: '',
  });
  // Combined errors.
  const errors = { ...internalErrors, ...initialErrors };
  const [description, setDescription] = useState(currentDescription ? currentDescription : '');
  const [ruleIds, setRuleIds] = useState<Array<string>>([]);

  // Note: this "unused" state is used to store deep segment state and trigger onChange callback.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [changedSegmentState, setChangedSegmentState] = useState<ChangeSegmentState | null>(null);

  const ruleOperatorRef = useRef<HTMLInputElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      addRulesetLabel: t(CDP_STRINGS.addRulesetLabel),
      createSegmentText: t(CDP_STRINGS.segmentBuilderCreateButton),
      deleteSegment: t(CDP_STRINGS.deleteSegmentDialogDeleteButton),
      detailsSectionName: t(CDP_STRINGS.segmentBuilderDetailsSection),
      errorCountNumber: t(CDP_STRINGS.errorCountNumber),
      errorRequiredRuleset: t(CDP_STRINGS.errorRequiredRuleset),
      idFieldErrorMessageEmpty: t(CDP_STRINGS.idFieldErrorMessageEmpty),
      idFieldErrorMessagePattern: t(CDP_STRINGS.idFieldErrorMessagePattern),
      nameFieldErrorMessageEmpty: t(CDP_STRINGS.nameFieldErrorMessageEmpty),
      requiredAttribute: t(CDP_STRINGS.requiredAttribute),
      requiredEventFeed: t(CDP_STRINGS.requiredEventFeed),
      requiredNumber: t(CDP_STRINGS.requiredNumber),
      requiredRule: t(CDP_STRINGS.requiredRule),
      requiredValue: t(CDP_STRINGS.requiredValue),
      ruleButtonText: t(CDP_STRINGS.ruleButtonTextAdd),
      rulesetButtonTextAdd: t(CDP_STRINGS.rulesetButtonTextAdd),
      rulesetButtonTextDelete: t(CDP_STRINGS.rulesetButtonTextDelete),
      rulesetTitleDataIntegration: t(CDP_STRINGS.rulesetTitleDataIntegration),
      rulesetTitleWebsiteBehavior: t(CDP_STRINGS.rulesetTitleWebsiteBehavior),
      rulesSectionName: t(CDP_STRINGS.segmentBuilderRulesSection),
      saveSegmentText: t(CDP_STRINGS.saveChanges),
      toastErrorMessage: t(CDP_STRINGS.toastErrorLabel),
      toastSuccessCreateMessage: t(CDP_STRINGS.toastSuccessCreateMessage),
      toastSuccessCreateMessageEnd: t(CDP_STRINGS.toastSuccessCreateMessageEnd),
      toastSuccessCreateTitle: t(CDP_STRINGS.toastSuccessCreateLabel),
      toastSuccessUpdateMessage: t(CDP_STRINGS.toastSuccessUpdateLabel),
    };
  }, [t]);

  // Form Validation.
  const validator = useFormValidation();
  const fieldValidations = useMemo((): Record<
    string,
    Array<AnyValidator> | ((a: string, input?: HTMLInputElement) => Array<AnyValidator>)
  > => {
    return {
      comparison: (errorKey: string) => [
        new NotEmptyValidator<object | null>(
          () => ({ [errorKey]: labels.requiredRule }),
          () => ({ [errorKey]: null }),
        ),
      ],
      count: (errorKey: string) => [
        new NotEmptyValidator<object | null>(
          () => ({ [errorKey]: labels.requiredNumber }),
          () => ({ [errorKey]: null }),
        ),
        new IntegerValidator<object | null>(
          () => ({ [errorKey]: labels.errorCountNumber }),
          () => ({ [errorKey]: null }),
        ),
      ],
      event: (errorKey: string) => [
        new NotEmptyValidator<object | null>(
          () => ({ [errorKey]: labels.requiredEventFeed }),
          () => ({ [errorKey]: null }),
        ),
      ],
      id: (errorKey: string): Array<AnyValidator> => {
        const idPatternValidator = new RegExpValidator<object | null>(
          // @ts-ignore.
          () => ({ [errorKey]: labels.idFieldErrorMessagePattern }),
          () => ({ [errorKey]: null }),
        );
        idPatternValidator.setExpr(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
        return [
          new NotEmptyValidator<object | null>(
            () => ({ [errorKey]: labels.idFieldErrorMessageEmpty }),
            () => ({ [errorKey]: null }),
          ),
          idPatternValidator,
        ];
      },
      name: (errorKey: string) => [
        new NotEmptyValidator<object | null>(
          () => ({ [errorKey]: labels.nameFieldErrorMessageEmpty }),
          () => ({ [errorKey]: null }),
        ),
      ],
      property: (errorKey: string, input?: HTMLInputElement): Array<AnyValidator> => {
        const type = input?.parentNode?.querySelector(`.sds-select-field `)?.getAttribute(`data-type`);
        return [
          new NotEmptyValidator<object | null>(
            () => ({ [errorKey]: type === `event-property` ? labels.requiredEventFeed : labels.requiredAttribute }),
            () => ({ [errorKey]: null }),
          ),
        ];
      },
      value: (errorKey: string) => [
        new NotEmptyValidator<object | null>(
          () => ({ [errorKey]: labels.requiredValue }),
          () => ({ [errorKey]: null }),
        ),
      ],
    };
  }, [
    labels.errorCountNumber,
    labels.idFieldErrorMessageEmpty,
    labels.idFieldErrorMessagePattern,
    labels.nameFieldErrorMessageEmpty,
    labels.requiredAttribute,
    labels.requiredEventFeed,
    labels.requiredNumber,
    labels.requiredRule,
    labels.requiredValue,
  ]);

  // Get the updated details.
  const handleDetailsInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.name.startsWith('name')) {
      setName(event.target.value);
    }
    if (event.target.name.startsWith('id')) {
      setId(event.target.value);
    }
    if (event.target.name.startsWith('description')) {
      setDescription(event.target.value);
    }
  };

  // turn the segment name into a slug and trim it to the accepted chars, then set that as the ID
  const createDevId = (event: ChangeEvent<HTMLInputElement>): void => {
    const segmentId = event.target.value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 20)
      .replace(/-$/g, '');
    if (id === '') {
      setId(segmentId);
      setInternalErrors({ ...errors, id: '' });
    }
  };

  const validateFields = useCallback(
    (fields: Array<Element>): FormFieldValidationResponse => {
      const toValidate: FormFieldList = fields
        .filter((field) => !isNull(field))
        .reduce((prev, field: Element) => {
          const id = field.getAttribute(`id`) as string;
          const key = id?.split(`-`).shift() as string;
          const validatorMap = property(key)(fieldValidations);
          if (!id || !key || !validatorMap) {
            return prev;
          }
          return {
            ...prev,
            [id]: {
              validators: (isFunction(validatorMap) ? validatorMap(id, field) : validatorMap) || [],
              // @ts-ignore.
              value: field.value,
            },
          };
        }, {});

      const { isValid, messages } = validator.validateList(toValidate);
      return { isValid, messages };
    },
    [fieldValidations, validator],
  );

  const getFields = useCallback((): Array<HTMLElement> => {
    const fields = Array.from(
      document.querySelector(`.segment-builder`)?.querySelectorAll(`input,textarea`) || [],
    ) as Array<HTMLElement>;
    fields.push(document.getElementById(`name-${internalId}`) as HTMLElement);
    fields.push(document.getElementById(`id-${internalId}`) as HTMLElement);
    fields.push(document.getElementById(`description-${internalId}`) as HTMLElement);
    return fields;
  }, [internalId]);

  // Note for future development. This was worked on by multiple developers and the way it detects
  // state and triggers the onChange handler is awkward. It's this way due to the very
  // complicated handler callback setup for all of the child components.
  useEffect((): void => {
    const fields = getFields();
    const response = validateFields(fields.filter((field) => !isUndefined(field) || !isNull(field)));

    // Non field based validation.
    if (conditions.rulesets.length === 0) {
      response.messages[`conditions-${internalId}`] = labels.errorRequiredRuleset;
    } else {
      response.messages[`conditions-${internalId}`] = null;
    }

    const changedSegment = {
      code: id,
      conditions: { [conditions.operator]: createRulesetsForAPI(conditions.rulesets) },
      description,
      internalId,
      name,
    };

    const currentRuleIds: Array<string> = [];
    conditions.rulesets.forEach((ruleset): void => {
      ruleset.rules.forEach((rule): void => {
        currentRuleIds.push(rule.id);
      });
    });
    const allRuleIds = uniq([...ruleIds, ...currentRuleIds]);
    setRuleIds(allRuleIds);

    // Null out any rule ids that don't have responses in the current validation.
    // This ensures that any deleted rules don't leave stray validation failures.
    allRuleIds.forEach((ruleId): void => {
      [`comparison`, `property`, `value`, `count`].forEach((fieldName): void => {
        if (!property(`${fieldName}-${ruleId}`)(response.messages)) {
          set(response.messages, `${fieldName}-${ruleId}`, null);
        }
      });
    });

    // Due to the way react calculates state (shallow) we can't use it to trigger the
    // onChange handler but rather compare states using deep comparison.
    setChangedSegmentState((oldSegmentState): ChangeSegmentState => {
      if (
        !isEqual(oldSegmentState, {
          response,
          segment: changedSegment,
        })
      ) {
        onChange(changedSegment, response);
      }
      return {
        response,
        segment: changedSegment,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions, description, id, name]);

  // Wait until schema and events are loaded if they were passed undefined.
  if (!schema || !events) {
    return <></>;
  }

  const addRuleSetItems: Array<DropdownItemData> = [
    {
      action: (): void => handleAddRuleset(`default`),
      key: `default`,
      label: `Data Integration`,
    },
    {
      action: (): void => handleAddRuleset(`event-property`),
      key: `event-property`,
      label: `Website Behavior`,
    },
  ];

  const conditionError = property(`conditions-${internalId}`)(errors) as string | undefined;
  const conditionErrorButtonProps: Partial<ButtonProps> = conditionError
    ? {
        ['aria-errormessage']: conditionError as string,
        ['aria-invalid']: true,
      }
    : {};

  return (
    <form className="segment-builder sds-row" key={internalId}>
      {isNumber(groupCount) && (
        <div className={`segment-builder__delete-button`}>
          <Button
            aria-label={labels.deleteSegment}
            disabled={groupCount <= 2}
            format={BUTTON_FORMAT_TRANSPARENT}
            icon={ICON_DELETE}
            onClick={() => {
              // Zero out any messages.
              const messages: Record<string, null> = {
                [`conditions-${internalId}`]: null,
              };
              const fields = getFields();
              fields.forEach((field) => {
                const fieldId = field.getAttribute(`id`);
                if (isString(fieldId)) {
                  messages[fieldId] = null;
                }
              });
              onDelete(internalId as string, messages);
            }}
            readOnly={groupCount <= 2}
            theme={BUTTON_THEME_DEFAULT}
            tooltip={{
              label: labels.deleteSegment,
              position: TOOLTIP_POSITION_TOP,
            }}
          />
        </div>
      )}
      <SegmentDetails
        data-id={SEGMENT_BUILDER_DATA_IDS.segmentDetailsSection}
        description={description}
        errors={errors}
        handleCreateDevId={createDevId}
        handleInputChange={handleDetailsInputChange}
        id={id}
        internalId={internalId as string}
        name={name}
        sectionName={labels.detailsSectionName}
      />

      <div data-id={SEGMENT_BUILDER_DATA_IDS.segmentRulesSection}>
        <h3 className="sds-row__title">
          {labels.rulesSectionName}
          <span className="sds-row__title-required">
            <span className="sr-only">This section is required.</span>
            <span>*</span>
          </span>
        </h3>
        {conditions.rulesets.map(
          (
            { operator, rules, type }: { operator: string; rules: Array<UISegmentRuleProperties>; type?: string },
            index: number,
          ) => {
            const rulesetId = index;
            return (
              <div
                className="segment-ruleset"
                data-id={SEGMENT_BUILDER_DATA_IDS.segmentRuleset}
                data-ruleset={rulesetId}
                key={`ruleset-${rulesetId}`}
              >
                <div className="sds-box">
                  <div className="section-actions segment-ruleset-actions">
                    <Tooltip label={labels.rulesetButtonTextDelete}>
                      <Button
                        classes="segment-rule__delete-ruleset"
                        data-id={SEGMENT_BUILDER_DATA_IDS.buttonDeleteRuleset}
                        data-ruleset={rulesetId}
                        format={BUTTON_FORMAT_TRANSPARENT}
                        icon={ICON_DELETE}
                        label={labels.rulesetButtonTextDelete}
                        onClick={handleDeleteRuleset}
                        theme={BUTTON_THEME_DEFAULT}
                      />
                    </Tooltip>
                  </div>
                  <h5 className="segment-ruleset-title">
                    {type === `event-property`
                      ? labels.rulesetTitleWebsiteBehavior
                      : labels.rulesetTitleDataIntegration}
                  </h5>
                  {rules.map((rule: UISegmentRuleProperties, index: number) => {
                    return (
                      <div
                        className="segment-rule"
                        data-rule={rule.id}
                        data-rule-index={index}
                        data-ruleset={rulesetId}
                        key={`ruleset-${rulesetId}-rule-${rule.id}`}
                      >
                        <SegmentRule
                          deleteFunction={handleDeleteRule}
                          errors={errors}
                          events={events}
                          isLastRule={rules.length === 1}
                          onChangeAttribute={(ref, v): void => {
                            const fields = [document.getElementById(`property-${rule.id}`)];
                            // Ensure the value is set before trying to validate it.
                            setTimeout(() => {
                              validateFields(fields as Array<HTMLElement>);
                              handleRuleSelectsChange(ref, v);
                            }, 50);
                          }}
                          onChangeInput={(evt): void => {
                            validateFields([evt.target as Element]);
                            // @ts-ignore.
                            handleRuleInputsChange(evt);
                          }}
                          onChangeRule={(ref, v): void => {
                            const fields = [document.getElementById(`comparison-${rule.id}`)];
                            // Ensure the value is set before trying to validate it.
                            setTimeout(() => {
                              validateFields(fields as Array<HTMLElement>);
                              handleRuleSelectsChange(ref, v);
                            }, 50);
                          }}
                          order={index}
                          ruleData={rule}
                          ruleset={rulesetId}
                          schema={schema}
                          type={type as string}
                        />
                        {index + 1 !== rules.length && (
                          <div className="divider__rules">
                            <span>{operator.toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="section-actions">
                    <Button
                      data-id={SEGMENT_BUILDER_DATA_IDS.buttonAddRule}
                      data-ruleset={rulesetId}
                      format={BUTTON_FORMAT_TRANSPARENT}
                      icon={ICON_ADD_ROUNDED}
                      label={labels.ruleButtonText}
                      onClick={handleAddRule}
                      text={labels.ruleButtonText}
                      theme={BUTTON_THEME_DEFAULT}
                    />
                    <SelectRuleOperator
                      callbackFunction={Number(rulesetId) === 0 ? handleRuleOperatorFirst : handleRuleOperatorLast}
                      preselectedOperator={operator}
                      ref={ruleOperatorRef}
                      ruleset={rulesetId}
                    />
                  </div>
                </div>
                {index + 1 !== conditions.rulesets.length && (
                  <div className="divider__rulesets">
                    <span>{(rulesetOperator as string).toUpperCase()}</span>
                  </div>
                )}
              </div>
            );
          },
        )}
        <div className="section-actions">
          <ActionMenu
            buttonLabel={`Add Ruleset`}
            // @ts-ignore.
            buttonProps={{
              ...conditionErrorButtonProps,
              [`data-id`]: SEGMENT_BUILDER_DATA_IDS.buttonAddRuleset,
              format: BUTTON_FORMAT_TRANSPARENT,
              icon: ICON_ADD,
              label: labels.addRulesetLabel,
              text: labels.addRulesetLabel,
              theme: BUTTON_THEME_DEFAULT,
            }}
            dropdownPosition={DROPDOWN_POSITION_LEFT}
            itemList={addRuleSetItems}
            sectionKey={`add-ruleset-menu`}
          />
          {conditions.rulesets.length >= 1 && (
            <SelectRulesetOperator
              callbackFunction={handleRuleSetOperator}
              preselectedOperator={rulesetOperator}
              ref={ruleOperatorRef}
            />
          )}
          {conditions.rulesets.length === 0 && <div style={{ flex: 1 }}></div>}
        </div>
        {conditionError && (
          <div className="segment-builder__error-message" id={`condition-error-${internalId}`}>
            {conditionError}
          </div>
        )}
      </div>
    </form>
  );
};
